import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Landmark, Upload, DollarSign, FileText } from "lucide-react";
import api from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const EMPTY = { broker_slug: "", period: thisMonth(), amount: "", currency: "USD", model: "", clients: "", note: "" };

export default function BrokerIncomePanel() {
  const [data, setData] = useState({ rows: [], total: 0, by_broker: {}, by_month: [] });
  const [brokers, setBrokers] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [csv, setCsv] = useState("");
  const [showCsv, setShowCsv] = useState(false);
  const [fromM, setFromM] = useState("");
  const [toM, setToM] = useState("");

  const load = () => api.get("/admin/broker-income").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/brokers").then((r) => setBrokers(r.data || [])).catch(() => {});
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.broker_slug) return toast.error("Pick a broker");
    try {
      await api.post("/admin/broker-income", {
        ...form, amount: Number(form.amount) || 0, clients: Number(form.clients) || 0,
      });
      toast.success("Commission logged");
      setForm({ ...EMPTY });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => { await api.delete(`/admin/broker-income/${id}`); load(); };

  const exportFile = async (fmt) => {
    try {
      const q = new URLSearchParams();
      if (fromM) q.set("start", fromM);
      if (toM) q.set("end", toM);
      const res = await api.get(`/admin/broker-income/export.${fmt}?${q.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `broker-income.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const importCsv = async () => {
    if (!csv.trim()) return;
    try {
      const { data: res } = await api.post("/admin/broker-income/import-csv", { csv });
      toast.success(`Imported ${res.added} row(s)${res.errors?.length ? `, ${res.errors.length} skipped` : ""}`);
      setCsv(""); setShowCsv(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
  };

  const inp = "rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-2.5 py-1.5 text-xs text-white focus:border-emerald-400/60 focus:outline-none";

  return (
    <div className="mt-6 card-edge overflow-hidden" data-testid="broker-income-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-500/10 px-6 py-4">
        <div className="flex items-center gap-2">
          <Landmark size={16} className="text-emerald-300" />
          <p className="overline">Broker income (affiliate / IB)</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-editorial text-2xl text-lime-300" data-testid="broker-income-total">{money(data.total)}</span>
          <button onClick={() => setShowCsv((v) => !v)} className="btn-edge-ghost text-xs" data-testid="broker-income-csv-toggle"><Upload size={13} /> Import CSV</button>
          <input type="month" value={fromM} onChange={(e) => setFromM(e.target.value)} title="Export from month" data-testid="broker-income-from" className="rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-2 py-1.5 text-xs text-white" />
          <input type="month" value={toM} onChange={(e) => setToM(e.target.value)} title="Export to month" data-testid="broker-income-to" className="rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-2 py-1.5 text-xs text-white" />
          <button onClick={() => exportFile("csv")} className="btn-edge-ghost text-xs" data-testid="broker-income-export-csv"><DollarSign size={13} /> Export CSV</button>
          <button onClick={() => exportFile("pdf")} className="btn-edge-ghost text-xs" data-testid="broker-income-export-pdf"><FileText size={13} /> Export PDF</button>
        </div>
      </div>

      <div className="p-6">
        {Object.keys(data.by_broker || {}).length > 0 && (
          <div className="mb-5 flex flex-wrap gap-2">
            {Object.entries(data.by_broker).map(([slug, amt]) => (
              <span key={slug} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-1 text-xs text-emerald-100/80" data-testid={`broker-income-chip-${slug}`}>
                <DollarSign size={11} className="text-lime-300" />{slug}: <strong className="text-white">{money(amt)}</strong>
              </span>
            ))}
          </div>
        )}

        {showCsv && (
          <div className="mb-5 rounded-lg border border-emerald-500/15 bg-[#0d2416]/40 p-4" data-testid="broker-income-csv-box">
            <p className="mb-2 text-xs text-emerald-100/60">Paste CSV with header: <code className="text-lime-300">broker_slug,period,amount,currency,model,clients,note</code> (period = YYYY-MM)</p>
            <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={4} data-testid="broker-income-csv-input"
              placeholder={"broker_slug,period,amount,currency,model,clients,note\nfxpro,2026-06,320,USD,CPA,4,June rebates"}
              className="w-full rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 font-mono-edge text-xs text-white focus:border-emerald-400/60 focus:outline-none" />
            <button onClick={importCsv} className="btn-edge mt-3 text-xs" data-testid="broker-income-csv-import"><Upload size={13} /> Import</button>
          </div>
        )}

        <form onSubmit={add} className="mb-5 flex flex-wrap items-end gap-2" data-testid="broker-income-form">
          <select value={form.broker_slug} onChange={(e) => setForm({ ...form, broker_slug: e.target.value })} className={inp} data-testid="broker-income-broker">
            <option value="">Broker…</option>
            {brokers.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
          </select>
          <input type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className={inp} data-testid="broker-income-period" />
          <input type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={`${inp} w-28`} data-testid="broker-income-amount" />
          <input placeholder="Model (CPA/rebate)" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className={`${inp} w-36`} data-testid="broker-income-model" />
          <input type="number" placeholder="Clients" value={form.clients} onChange={(e) => setForm({ ...form, clients: e.target.value })} className={`${inp} w-24`} data-testid="broker-income-clients" />
          <input placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={`${inp} flex-1 min-w-[120px]`} data-testid="broker-income-note" />
          <button type="submit" className="btn-edge text-xs" data-testid="broker-income-add"><Plus size={13} /> Log</button>
        </form>

        {data.rows.length === 0 ? (
          <p className="text-sm text-emerald-100/50">No broker commissions logged yet. Add them manually or import a CSV from your broker portal.</p>
        ) : (
          <div className="divide-y divide-emerald-500/8">
            {data.rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`broker-income-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-white">{r.broker_name || r.broker_slug} <span className="text-[11px] text-emerald-100/50">· {r.period}{r.model ? ` · ${r.model}` : ""}{r.clients ? ` · ${r.clients} client(s)` : ""}</span></p>
                  {r.note && <p className="truncate text-[11px] text-emerald-100/50">{r.note}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-editorial text-lime-300">{money(r.amount)}</span>
                  <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-500" data-testid={`broker-income-del-${r.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
